import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";

export const LOOM_FACE_OWNER_WING = 0;
export const LOOM_FACE_OWNER_FUSELAGE = 1;

export interface LoomExactSourceFaceEvidence {
  faceCornerParameters?: Float32Array;
  faceOwners: Uint8Array;
  seamVertexRings: readonly Uint32Array[];
  source: "legacy-single-loop" | "verified-v3-union-receipt";
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function samePair(left: readonly number[], right: readonly number[]): boolean {
  return left.length === 2 && right.length === 2
    && left.every((item) => right.includes(item));
}

function legacyFaceOwners(
  asset: LoomNativeHybridAsset,
  planeEpsilonM: number,
): Uint8Array {
  const { faces, positions } = asset.surface;
  const faceCount = faces.length / 3;
  const seam = asset.seam.wingVertexIndices;
  if (faceCount === 0 || seam.length < 3 || seam.length !== asset.seam.fuselageVertexIndices.length
      || !seam.every((vertex, index) => vertex === asset.seam.fuselageVertexIndices[index])) {
    throw new Error("accepted source seam identity is invalid");
  }
  const seamEdges = new Set<string>();
  for (let index = 0; index < seam.length; index += 1) {
    seamEdges.add(edgeKey(seam[index]!, seam[(index + 1) % seam.length]!));
  }
  const edgeFace = new Map<string, number>();
  const neighbours = Array.from({ length: faceCount }, () => [] as number[]);
  for (let face = 0; face < faceCount; face += 1) {
    const vertices = [faces[face * 3]!, faces[face * 3 + 1]!, faces[face * 3 + 2]!];
    for (const [a, b] of [[vertices[0]!, vertices[1]!], [vertices[1]!, vertices[2]!], [vertices[2]!, vertices[0]!]] as const) {
      const key = edgeKey(a, b);
      if (seamEdges.has(key)) continue;
      const adjacent = edgeFace.get(key);
      if (adjacent === undefined) edgeFace.set(key, face);
      else {
        neighbours[face]!.push(adjacent);
        neighbours[adjacent]!.push(face);
      }
    }
  }
  const component = new Int32Array(faceCount).fill(-1);
  const components: number[][] = [];
  for (let start = 0; start < faceCount; start += 1) {
    if (component[start] !== -1) continue;
    const id = components.length;
    const members: number[] = [];
    const pending = [start];
    component[start] = id;
    while (pending.length) {
      const face = pending.pop()!;
      members.push(face);
      for (const next of neighbours[face]!) if (component[next] === -1) {
        component[next] = id;
        pending.push(next);
      }
    }
    components.push(members);
  }
  if (components.length !== 2) throw new Error("accepted seam does not separate two exact source patches");
  const crossesPort = components.map((members) => members.some((face) => (
    [faces[face * 3]!, faces[face * 3 + 1]!, faces[face * 3 + 2]!]
      .some((vertex) => positions[vertex * 3 + 1]! < -planeEpsilonM)
  )));
  if (crossesPort.filter(Boolean).length !== 1) {
    throw new Error("accepted exact source patches cannot be identified without ambiguity");
  }
  const fuselageComponent = crossesPort.findIndex(Boolean);
  const owners = new Uint8Array(faceCount);
  for (let face = 0; face < faceCount; face += 1) {
    owners[face] = component[face] === fuselageComponent ? LOOM_FACE_OWNER_FUSELAGE : LOOM_FACE_OWNER_WING;
  }
  return owners;
}

function definitionSourceIds(asset: LoomNativeHybridAsset): readonly [string, string] {
  const definition = asset.acceptedGeometry.header.definitionBinding?.definition as {
    wing?: { sourceNodeId?: unknown };
    fuselage?: { sourceNodeId?: unknown };
  } | undefined;
  const wing = definition?.wing?.sourceNodeId;
  const fuselage = definition?.fuselage?.sourceNodeId;
  if (typeof wing !== "string" || typeof fuselage !== "string" || wing === fuselage) {
    throw new Error("v3 exact face ownership is not bound to one wing and one fuselage source");
  }
  return [wing, fuselage];
}

function v3FaceEvidence(asset: LoomNativeHybridAsset): LoomExactSourceFaceEvidence {
  const accepted = asset.acceptedGeometry;
  const union = accepted.unionReceipt;
  if (asset.integrity.status !== "verified" || accepted.header.schema !== "loom.accepted-mesh/v3"
      || asset.header.acceptedGeometry.schema !== "loom.accepted-mesh/v3" || !union) {
    throw new Error("v3 exact face ownership requires a verified union receipt");
  }
  const [wingId, fuselageId] = definitionSourceIds(asset);
  const sourceNodes = accepted.header.sourceNodes;
  if (sourceNodes.length !== 2 || new Set(sourceNodes.map(({ sourceNodeId }) => sourceNodeId)).size !== 2) {
    throw new Error("v3 exact face ownership has an unbound source-node table");
  }
  const wingIndex = sourceNodes.findIndex(({ sourceNodeId }) => sourceNodeId === wingId);
  const fuselageIndex = sourceNodes.findIndex(({ sourceNodeId }) => sourceNodeId === fuselageId);
  if (wingIndex < 0 || fuselageIndex < 0) {
    throw new Error("v3 exact face ownership source nodes disagree with the accepted definition");
  }
  const { faces } = asset.surface;
  const faceCount = faces.length / 3;
  if (faces.length % 3 !== 0 || union.faceSourceNodeIndices.length !== faceCount
      || union.facePatchIndices.length !== faceCount
      || union.faceCornerParameters.length !== faceCount * 3 * 2
      || !union.faceCornerParameters.every(Number.isFinite)) {
    throw new Error("v3 exact face ownership or UV evidence has an invalid extent");
  }
  const owners = new Uint8Array(faceCount);
  const usedSources = new Set<number>();
  for (let face = 0; face < faceCount; face += 1) {
    const source = union.faceSourceNodeIndices[face]!;
    const patch = union.facePatchIndices[face]!;
    if (source !== wingIndex && source !== fuselageIndex) {
      throw new Error("v3 exact face ownership references a source outside the selected pair");
    }
    if (patch >= accepted.header.patches.length
        || accepted.header.patches[patch]!.sourceNodeIndex !== source
        || accepted.header.patches[patch]!.evaluatorKind !== "exact-parametric") {
      throw new Error("v3 exact face ownership has a stale patch binding");
    }
    usedSources.add(source);
    owners[face] = source === wingIndex ? LOOM_FACE_OWNER_WING : LOOM_FACE_OWNER_FUSELAGE;
  }
  if (usedSources.size !== 2) throw new Error("v3 exact face ownership does not use both selected sources");

  const edgeFaces = new Map<string, number[]>();
  for (let face = 0; face < faceCount; face += 1) {
    const vertices = [faces[face * 3]!, faces[face * 3 + 1]!, faces[face * 3 + 2]!];
    if (new Set(vertices).size !== 3 || vertices.some((vertex) => vertex >= asset.surface.positions.length / 3)) {
      throw new Error("v3 exact face ownership contains an invalid surface face");
    }
    for (const [a, b] of [[vertices[0]!, vertices[1]!], [vertices[1]!, vertices[2]!], [vertices[2]!, vertices[0]!]] as const) {
      const key = edgeKey(a, b);
      edgeFaces.set(key, [...(edgeFaces.get(key) ?? []), face]);
    }
  }
  if ([...edgeFaces.values()].some((incidence) => incidence.length !== 2)) {
    throw new Error("v3 exact face ownership surface is not closed and manifold");
  }
  const exactCrossSourceEdges = new Set([...edgeFaces.entries()]
    .filter(([, incidence]) => owners[incidence[0]!] !== owners[incidence[1]!])
    .map(([key]) => key));
  const declaredEdges = new Set<string>();
  const declaredVertices = new Set<number>();
  const sourcePair = [wingIndex, fuselageIndex];
  for (const seam of union.sharedSeams) {
    if (seam.vertexIndices.length < 3 || seam.parametersA.length !== seam.vertexIndices.length * 2
        || seam.parametersB.length !== seam.vertexIndices.length * 2
        || !seam.parametersA.every(Number.isFinite) || !seam.parametersB.every(Number.isFinite)
        || !samePair(sourcePair, seam.sourceNodeIndices)
        || seam.patchIndices.some((patch) => patch >= accepted.header.patches.length)
        || !samePair(
          seam.patchIndices.map((patch) => accepted.header.patches[patch]!.sourceNodeIndex),
          seam.sourceNodeIndices,
        )
        || !Number.isFinite(seam.maxSurfaceGapM) || seam.maxSurfaceGapM < 0) {
      throw new Error("v3 exact shared seam is malformed or unbound");
    }
    for (let index = 0; index < seam.vertexIndices.length; index += 1) {
      const vertex = seam.vertexIndices[index]!;
      if (declaredVertices.has(vertex)) throw new Error("v3 exact shared seam loops overlap");
      declaredVertices.add(vertex);
      const key = edgeKey(vertex, seam.vertexIndices[(index + 1) % seam.vertexIndices.length]!);
      if (declaredEdges.has(key)) throw new Error("v3 exact shared seam edges overlap");
      const adjacent = edgeFaces.get(key) ?? [];
      if (adjacent.length !== 2
          || !samePair(adjacent.map((face) => union.faceSourceNodeIndices[face]!), seam.sourceNodeIndices)
          || !samePair(adjacent.map((face) => union.facePatchIndices[face]!), seam.patchIndices)) {
        throw new Error("v3 exact shared seam is not bound to its declared source patches");
      }
      declaredEdges.add(key);
    }
  }
  const pair = union.header.pairReceipts;
  if (union.sharedSeams.length < 1 || pair.length !== 1
      || !samePair(sourcePair, pair[0]!.sourceNodeIndices)
      || pair[0]!.loopCount !== union.sharedSeams.length
      || union.header.qc.seamEdgeCount !== declaredEdges.size
      || declaredEdges.size !== exactCrossSourceEdges.size
      || [...declaredEdges].some((key) => !exactCrossSourceEdges.has(key))) {
    throw new Error("v3 exact shared seam arrays do not cover every source boundary");
  }
  return {
    faceCornerParameters: new Float32Array(union.faceCornerParameters),
    faceOwners: owners,
    seamVertexRings: union.sharedSeams.map(({ vertexIndices }) => new Uint32Array(vertexIndices)),
    source: "verified-v3-union-receipt",
  };
}

export function deriveLoomExactSourceFaceEvidence(
  asset: LoomNativeHybridAsset,
  planeEpsilonM: number,
): LoomExactSourceFaceEvidence {
  if (asset.acceptedGeometry.header.schema === "loom.accepted-mesh/v3") return v3FaceEvidence(asset);
  return {
    faceOwners: legacyFaceOwners(asset, planeEpsilonM),
    seamVertexRings: [new Uint32Array(asset.seam.wingVertexIndices)],
    source: "legacy-single-loop",
  };
}

export function deriveLoomExactSourceFaceOwners(
  asset: LoomNativeHybridAsset,
  planeEpsilonM: number,
): Uint8Array {
  return deriveLoomExactSourceFaceEvidence(asset, planeEpsilonM).faceOwners;
}
